/**
 * src/v2/services/geolinkRulesService.ts
 * Frontend service for GeoLink rule history and simulation.
 *
 * Endpoints proxied through GeoTrust backend → GeoLink:
 *   GET  /api/v2/geolink/pending-rules         ← non-deposit pending rules
 *   POST /api/v2/geolink/pending-rules/:id/reject
 *   POST /api/v2/geolink/pending-rules/:id/complete
 *   GET  /api/v2/geolink/completed             ← completed rule history
 *   GET  /api/v2/geolink/rejected              ← rejected rule history
 *   POST /api/v2/geolink/simulate              ← readonly function simulation
 */

const BASE = '';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface GeoLinkPendingRule {
  rule_id: number;
  rule_name: string;
  function_name: string;
  contract_address: string;
  matched_public_key: string;
  update_id: number;
  received_at: string;
  status: string;
  message?: string | null;
  location?: { latitude: number; longitude: number } | null;
  parameters?: Record<string, any> | null;
  /** Wallet that authored the rule. Surfaced by GeoLink per the multi-tenant
   *  scoping integration guide §4. Used by panels to filter or annotate
   *  rows the connected wallet doesn't own. */
  created_by_public_key?: string | null;
}

export interface GeoLinkCompletedRule {
  rule_id: number;
  rule_name: string;
  function_name: string;
  contract_address: string;
  matched_public_key: string;
  transaction_hash: string | null;
  completed_at: string;
  received_at: string;
  location?: { latitude: number; longitude: number } | null;
  parameters?: Record<string, any> | null;
  /** Surfaced by GeoLink per the scoping integration guide §4. */
  created_by_public_key?: string | null;
}

export interface GeoLinkRejectedRule {
  rule_id: number;
  rule_name: string;
  function_name: string;
  contract_address: string;
  matched_public_key: string;
  received_at: string;
  location?: { latitude: number; longitude: number } | null;
  parameters?: Record<string, any> | null;
  /** Surfaced by GeoLink per the scoping integration guide §4. */
  created_by_public_key?: string | null;
}

export interface SimulateResult {
  success: boolean;
  result?: any;
  is_simulation?: boolean;
  function_name?: string;
  error?: string;
  message?: string;
}

// ── API functions ─────────────────────────────────────────────────────────────

export async function listPendingRules(walletPublicKey?: string | null): Promise<{
  success: boolean;
  pending_rules?: GeoLinkPendingRule[];
  error?: string;
}> {
  const url = new URL(`${BASE}/api/v2/geolink/pending-rules`, window.location.origin);
  if (walletPublicKey) url.searchParams.set('public_key', walletPublicKey);
  const res = await fetch(url.toString());
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function rejectPendingRule(ruleId: number | string, matchedPublicKey: string): Promise<{
  success: boolean;
  error?: string;
}> {
  const res = await fetch(`${BASE}/api/v2/geolink/pending-rules/${encodeURIComponent(ruleId)}/reject`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ matched_public_key: matchedPublicKey }),
  });
  return res.json();
}

export async function completePendingRule(ruleId: number | string, matchedPublicKey: string, transactionHash?: string): Promise<{
  success: boolean;
  error?: string;
}> {
  const res = await fetch(`${BASE}/api/v2/geolink/pending-rules/${encodeURIComponent(ruleId)}/complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ matched_public_key: matchedPublicKey, transaction_hash: transactionHash }),
  });
  return res.json();
}

/**
 * Fetch completed rule history.
 *
 * Pass `walletPublicKey` to scope to the connected wallet per the
 * multi-tenant scoping integration guide §3.1 — GeoLink filters on
 * `matched_public_key` for completed/rejected lists, so the same wallet
 * key the panel uses for pending rules is the right one here too. Without
 * a `walletPublicKey` we fall back to the server's default (admin-style
 * unscoped view) for back-compat with admin panels.
 */
export async function listCompletedRules(
  limit = 100,
  walletPublicKey?: string | null,
): Promise<{
  success: boolean;
  completed_rules?: GeoLinkCompletedRule[];
  error?: string;
}> {
  const url = new URL(`${BASE}/api/v2/geolink/completed`, window.location.origin);
  url.searchParams.set('limit', String(limit));
  if (walletPublicKey) url.searchParams.set('public_key', walletPublicKey);
  const res = await fetch(url.toString());
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
}

/**
 * Fetch rejected rule history. Same scoping semantics as
 * `listCompletedRules` — see the docstring above for details.
 */
export async function listRejectedRules(
  limit = 100,
  walletPublicKey?: string | null,
): Promise<{
  success: boolean;
  rejected_rules?: GeoLinkRejectedRule[];
  error?: string;
}> {
  const url = new URL(`${BASE}/api/v2/geolink/rejected`, window.location.origin);
  url.searchParams.set('limit', String(limit));
  if (walletPublicKey) url.searchParams.set('public_key', walletPublicKey);
  const res = await fetch(url.toString());
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
}

/**
 * Simulate (execute) a readonly contract function via GeoLink.
 * GeoLink auto-detects readonly functions (get_, is_, has_, etc.) and
 * returns the simulation result without creating a transaction.
 */
export async function simulateContractFunction(
  contractId: string,
  functionName: string,
  parameters: Record<string, any>,
  userPublicKey: string,
): Promise<SimulateResult> {
  const res = await fetch(`${BASE}/api/v2/geolink/simulate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ contractId, functionName, parameters, userPublicKey }),
  });
  const data = await res.json().catch(() => ({ success: false, error: 'Invalid response' }));
  return data;
}
